import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Modal } from 'grabbitbot-legacy-ui'
import { tradingClient } from '../../api'
import type { CoinConfig, ConstantEntry, Strategy } from '../../api/tradingTypes'
import {
  LOGIC_LABELS,
  MAX_MOP_LABELS,
  TRADING_MODES_WITH_STOPPED,
  detailsDefaultAutoBuyAmountLabel,
  detailsDefaultAutoBuyAssetLabel,
  detailsDefaultAutoBuyModeLabel,
  detailsDefaultAutoBuyUsdAmountLabel,
  getConstantLabel,
  getFieldLabel,
  getStringFieldLabel,
} from './coinConfigLabels'
import { AUTO_BUY_MODES, OFF_MODE_TRIGGERS, isUsd } from './tradingConstants'

const PAIR_TYPES: Record<string, string> = { NCP: 'Normal Coin Pair', RCP: 'Reverse Coin Pair', VCP: 'Virtual Coin Pair' }

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}
function constantsOf(source: Record<string, unknown>, field: string): ConstantEntry[] {
  const value = source[field]
  return Array.isArray(value) ? (value as ConstantEntry[]) : []
}

/**
 * Equivalent of legacy `V3CoinDetailsPopupComponent`: a read-only
 * Effective / Custom / Default / Trader comparison of one coin pair's config
 * (Trader column only for a subscribed pair). Nothing here is editable.
 */
export function CoinDetailsPopup({ coinConfig, onClose }: { coinConfig: CoinConfig | null; onClose: () => void }) {
  const [strategy, setStrategy] = useState<Strategy | null>(null)

  useEffect(() => {
    setStrategy(null)
    if (!coinConfig?.strategyId) return
    let cancelled = false
    tradingClient
      .viewStrategy(coinConfig.strategyId)
      .then((s) => {
        if (!cancelled) setStrategy(s)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [coinConfig])

  if (!coinConfig) return null
  const c = coinConfig
  const effective = asRecord(c.effective)
  const own = asRecord(c)
  const def = asRecord(c.default)
  const trader = asRecord(c.trader)
  const synced = Number(c.syncState) > 0
  const span = synced ? 4 : 3
  const coin2Unit = ` ${c.coin2}`

  const row = (label: ReactNode, cells: ReactNode[]) => (
    <tr>
      <th scope="row">{label}</th>
      {cells.slice(0, synced ? 4 : 3).map((cell, i) => (
        <td key={i}>{cell}</td>
      ))}
    </tr>
  )
  const wide = (label: ReactNode, content: ReactNode) => (
    <tr>
      <th scope="row">{label}</th>
      <td colSpan={span}>{content}</td>
    </tr>
  )
  const field = (name: string, map?: Record<string, string>, postfix = '') => [
    getFieldLabel(effective[name], postfix, map),
    getFieldLabel(own[name], postfix, map),
    getFieldLabel(def[name], postfix, map),
    getFieldLabel(trader[name], postfix, map),
  ]

  function valueRow(label: string, valueField: string, constantField: string, constantsField: string, unit: string, scopedDefault: boolean) {
    const constant = String(own[constantField] ?? '')
    const withConst = (text: string) => `${constant} - ${text}`
    const defaultKey = scopedDefault ? `${constant}::${c.coin2}` : constant
    return row(
      <>
        {label}
        {constant && <span className="portal-muted-small">(by constant)</span>}
      </>,
      [
        `${String(effective[valueField] ?? '')}${unit}`,
        constant
          ? withConst(getConstantLabel(constant, constantsOf(own, constantsField), unit))
          : synced
            ? "Use trader's value"
            : `${String(own[valueField] ?? '')}${unit}`,
        constant ? withConst(getConstantLabel(defaultKey, constantsOf(def, constantsField), unit)) : 'n/a',
        constant
          ? withConst(getConstantLabel(constant, constantsOf(trader, constantsField), unit))
          : `${String(own[valueField] ?? '')}${unit}`,
      ],
    )
  }

  const effAsset = String(effective.autoBuyAsset ?? '')
  const defaultAsset = detailsDefaultAutoBuyAssetLabel(c)

  return (
    <Modal open title={`${c.coin1}${c.coin2} details`} onClose={onClose} className="portal-modal-wide">
      <div className="portal-table-scroll" data-testid="coin-details-popup">
        {c.preferences && (
          <table className="portal-edit-table">
            <tbody>
              <tr>
                <th scope="row">Exchange status</th>
                <td>{String(c.preferences.status ?? '')}</td>
              </tr>
              <tr>
                <th scope="row">Type</th>
                <td>{PAIR_TYPES[String(c.preferences.type ?? '')] ?? ''}</td>
              </tr>
            </tbody>
          </table>
        )}
        <table className="portal-edit-table">
          <thead>
            <tr>
              <th aria-label="Setting" />
              <th>Effective</th>
              <th>Custom</th>
              <th>Default</th>
              {synced && <th>Trader</th>}
            </tr>
          </thead>
          <tbody>
            {wide('Config source', c.strategyId ? `Strategy subscription${Number(c.syncState) === 1 ? ' (own)' : ''}` : 'Custom config')}
            {c.strategyId &&
              wide(
                'Strategy',
                <Link to={`/strategy/spot/${c.strategyId}`}>
                  {strategy ? (strategy.userState === 1 ? strategy.name : strategy.realPublicName) : c.strategyId}
                </Link>,
              )}
            {c.groupId && wide('Group', c.groupId)}
            {wide('Trading Mode', TRADING_MODES_WITH_STOPPED[String(c.tradingMode)] ?? String(c.tradingMode ?? ''))}
            {valueRow('Bid', 'bid', 'bidConstant', 'bidConstants', coin2Unit, true)}
            {valueRow('Grid Up', 'gridUp', 'gridUpConstant', 'gridConstants', '%', false)}
            {valueRow('Grid Down', 'gridDown', 'gridDownConstant', 'gridConstants', '%', false)}
            {row('Lock Off Trading Mode', field('lockOnOffMode', LOGIC_LABELS))}
            {row('Max MOP count', field('maxMopCount', MAX_MOP_LABELS))}
            {row('Off handler', field('offModeTrigger', OFF_MODE_TRIGGERS))}
            {row('Run off handler on non-current strategy MOPS too', [
              ...field('offHandlerRunOnNonSameStrategyPositions', LOGIC_LABELS).slice(0, 3),
              'n/a',
            ])}
            {row('Auto Buy Mode', [
              getFieldLabel(effective.autoBuyMode, '', AUTO_BUY_MODES),
              getFieldLabel(own.autoBuyMode, '', AUTO_BUY_MODES),
              detailsDefaultAutoBuyModeLabel(c, AUTO_BUY_MODES),
              'n/a',
            ])}
            {row('Auto Buy Amount', [
              isUsd(effAsset)
                ? getFieldLabel(effective.autoBuyUsdAmount, ` ${effAsset}`)
                : getFieldLabel(effective.autoBuyAmount, coin2Unit),
              isUsd(effAsset) ? getFieldLabel(own.autoBuyUsdAmount, ` ${effAsset}`) : getFieldLabel(own.autoBuyAmount, coin2Unit),
              isUsd(defaultAsset) ? (
                <>
                  {detailsDefaultAutoBuyUsdAmountLabel(c)}
                  {!isUsd(c.coin2) && <span className="portal-muted-small">{detailsDefaultAutoBuyAmountLabel(c)}</span>}
                </>
              ) : (
                <>
                  {!isUsd(c.coin2) && detailsDefaultAutoBuyAmountLabel(c)}
                  <span className="portal-muted-small">{detailsDefaultAutoBuyUsdAmountLabel(c)}</span>
                </>
              ),
              'n/a',
            ])}
            {row('Auto Buy Asset', [
              getStringFieldLabel(effective.autoBuyAsset as string | null | undefined),
              getStringFieldLabel(own.autoBuyAsset as string | null | undefined),
              defaultAsset,
              'n/a',
            ])}
            {constantSection('Bid constants', constantsOf(effective, 'bidConstants'), (key) => [
              getConstantLabel(key, constantsOf(effective, 'bidConstants'), coin2Unit),
              getConstantLabel(key, constantsOf(own, 'bidConstants'), coin2Unit),
              getConstantLabel(`${key}::${c.coin2}`, constantsOf(def, 'bidConstants'), coin2Unit),
              getConstantLabel(key, constantsOf(trader, 'bidConstants'), coin2Unit),
            ])}
            {constantSection('Grid constants', constantsOf(effective, 'gridConstants'), (key) => [
              getConstantLabel(key, constantsOf(effective, 'gridConstants'), '%'),
              getConstantLabel(key, constantsOf(own, 'gridConstants'), '%'),
              getConstantLabel(key, constantsOf(def, 'gridConstants'), '%'),
              getConstantLabel(key, constantsOf(trader, 'gridConstants'), '%'),
            ])}
          </tbody>
        </table>
      </div>
      <div className="portal-form-footer">
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  )

  function constantSection(title: string, constants: ConstantEntry[], cells: (key: string) => string[]) {
    return (
      <>
        {constants.length === 0 ? wide(title, 'No constants are available for this coin pair') : wide(title, '')}
        {constants.map((constant) => (
          <tr key={`${title}-${String(constant.key)}`}>
            <th scope="row">{String(constant.key)}</th>
            {cells(String(constant.key))
              .slice(0, synced ? 4 : 3)
              .map((cell, i) => (
                <td key={i}>{cell}</td>
              ))}
          </tr>
        ))}
      </>
    )
  }
}
